import { useRef, useEffect, useState } from 'react'
import { SineMarkdown } from './SineMarkdown'

/**
 * SmoothStreamingText — renders streaming text with a smooth character-by-character
 * reveal animation instead of blocky batch updates.
 *
 * Strategy:
 * - When `isStreaming` is true, we use a local display buffer that gradually catches up
 *   to the full `content` using requestAnimationFrame.
 * - Characters are revealed at a configurable speed (chars per frame).
 * - When `isStreaming` becomes false, we immediately show the full content.
 * - While streaming, render stable plain text. Parsing partial markdown on every
 *   token can reshape the DOM repeatedly and make the text flicker.
 * - When streaming finishes, render the final content as markdown.
 */

interface SmoothStreamingTextProps {
  content: string
  isStreaming?: boolean
  animateOnMount?: boolean
  animationKey?: string | number
}

// Characters to reveal per animation frame (higher = faster)
const CHARS_PER_FRAME = 14
// If we fall behind by more than this, catch up instantly
const MAX_LAG = 80

export function SmoothStreamingText({
  content,
  isStreaming = false,
  animateOnMount = false,
  animationKey,
}: SmoothStreamingTextProps) {
  const [displayLength, setDisplayLength] = useState(content.length)
  const [animationDone, setAnimationDone] = useState(false)
  const rafRef = useRef<number | null>(null)
  const targetLengthRef = useRef(0)
  const animationKeyRef = useRef<string | number | undefined>(animationKey)
  const shouldAnimate = isStreaming || animateOnMount

  // Track the target length
  useEffect(() => {
    targetLengthRef.current = content.length
  }, [content])

  // Animation loop: gradually reveal characters
  useEffect(() => {
    if (!shouldAnimate) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      setDisplayLength(content.length)
      setAnimationDone(true)
      return
    }

    setAnimationDone(false)

    // Only restart from zero for a genuinely new message/animation. During
    // streaming, content.length changes on every chunk; resetting here makes
    // the whole assistant response blink and reflow over and over.
    if (animationKeyRef.current !== animationKey) {
      animationKeyRef.current = animationKey
      setDisplayLength(0)
    }

    const animate = () => {
      setDisplayLength(prev => {
        const target = targetLengthRef.current
        if (prev >= target) return prev

        const lag = target - prev
        // If we're too far behind, catch up faster
        const step = lag > MAX_LAG ? Math.ceil(lag / 3) : CHARS_PER_FRAME
        return Math.min(prev + step, target)
      })
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [content.length, shouldAnimate, animationKey])

  // Once animation is done and not streaming, render static content
  // This prevents re-renders that break text selection
  if (animationDone && !isStreaming) {
    return (
      <div className="smooth-streaming-wrapper" style={{ userSelect: 'text' }}>
        <SineMarkdown>{content}</SineMarkdown>
      </div>
    )
  }

  // The text to display — either the gradually revealed portion or the full content
  const displayText = shouldAnimate ? content.slice(0, displayLength) : content

  if (shouldAnimate) {
    return (
      <div className="smooth-streaming-wrapper" aria-live="polite">
        <SineMarkdown>{displayText}</SineMarkdown>
      </div>
    )
  }

  return (
    <div className="smooth-streaming-wrapper">
      <SineMarkdown>{displayText}</SineMarkdown>
    </div>
  )
}
